import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import PlumoLogo from './PlumoLogo';
import { useCart } from '../contexts/CartContext';

// Rotating Announcement Bar Component
const RotatingAnnouncementBar = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [isExpired, setIsExpired] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFading, setIsFading] = useState(false);

  // Countdown logic
  useEffect(() => {
    const targetDate = new Date('2026-05-26T23:59:59-07:00');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000); // Update every minute (no seconds)

    return () => clearInterval(interval);
  }, []);

  // Message rotation logic - pause on hover
  useEffect(() => {
    if (isExpired || isHovered) return;

    const interval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % 3);
        setIsFading(false);
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, [isExpired, isHovered]);

  const formatCountdown = () => {
    const { days, hours, minutes } = timeLeft;
    if (days > 0) {
      return `${days}D ${hours}H ${minutes}M`;
    } else if (hours > 0) {
      return `${hours}H ${minutes}M`;
    } else {
      return `${minutes}M`;
    }
  };

  const barStyle = {
    backgroundColor: '#0D0D0B',
    padding: '10px 24px',
    width: '100%',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'block',
    minHeight: '36px',
  };

  const textStyle = {
    fontFamily: "'Space Mono', monospace",
    fontWeight: 400,
    fontSize: '11px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#FDFAF4',
    textAlign: 'center',
    lineHeight: 1,
    margin: 0,
    whiteSpace: 'nowrap',
    opacity: isFading ? 0 : 1,
    transition: 'opacity 300ms ease',
  };

  // If expired, show only shipping message
  if (isExpired) {
    return (
      <Link
        to="/shop"
        style={barStyle}
        data-testid="announcement-bar"
      >
        <p style={textStyle}>
          FREE US SHIPPING OVER $50
        </p>
      </Link>
    );
  }

  const countdown = formatCountdown();
  
  const messages = [
    `MEMORIAL DAY · 60% OFF EVERYTHING · ENDS IN ${countdown}`,
    `THE THINGS WORTH CARRYING · NOW UP TO 60% OFF`,
    `FREE US SHIPPING OVER $50 · 60% OFF ENDS ${countdown}`,
  ];

  return (
    <Link
      to="/shop"
      style={barStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="announcement-bar"
    >
      <p style={textStyle}>
        {messages[currentMessageIndex]}
      </p>
    </Link>
  );
};

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();
  const { cartCount, openCheckout } = useCart();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/shop', label: 'SHOP' },
    { path: '/about', label: 'ABOUT' },
    { path: '/shipping', label: 'CONTACT' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Single Rotating Announcement Bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <RotatingAnnouncementBar />
      </div>

      {/* Navigation */}
      <nav 
        className="sticky top-0 w-full bg-white/98 backdrop-blur-sm z-50" 
        data-testid="main-nav"
        style={{ top: '36px' }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* 16px top/bottom padding for larger logo */}
          <div className="flex items-center justify-between" style={{ height: '88px', paddingTop: '16px', paddingBottom: '16px' }}>
            {/* Desktop Nav Left - UPDATE 4: 13px, weight 500, uppercase, 32px gap */}
            <div className="hidden md:flex items-center" style={{ gap: '32px' }}>
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="transition-colors hover:opacity-60"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.15em',
                    color: '#0D0D0B',
                    textTransform: 'uppercase',
                  }}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Logo - Center - UPDATE 1: Custom SVG, 180px desktop, 140px mobile */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <PlumoLogo width={180} className="hidden md:block" />
              <PlumoLogo width={140} className="block md:hidden" />
            </div>

            {/* Cart - Right - Clickable link with dynamic count */}
            <div className="hidden md:block">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  openCheckout();
                }}
                className="transition-opacity hover:opacity-60"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0.15em',
                  color: '#0D0D0B',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                data-testid="cart-button"
              >
                CART ({cartCount})
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 ml-auto"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" style={{ color: '#0D0D0B' }} />
              ) : (
                <Menu className="w-5 h-5" style={{ color: '#0D0D0B' }} />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-black/5 py-8" data-testid="mobile-menu">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.15em',
                    color: '#0D0D0B',
                    textTransform: 'uppercase',
                  }}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-plumo-bone mt-32" data-testid="footer">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-16">
            {/* Brand - UPDATE 1: Custom SVG logo, 140px */}
            <div className="md:col-span-1">
              <PlumoLogo width={140} />
            </div>

            {/* Shop Links - UPDATE 4: 11px headers, 14px links */}
            <div>
              <h4 
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  color: '#0D0D0B',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                }}
              >
                SHOP
              </h4>
              <ul className="space-y-3">
                <li><Link to="/shop?category=necklaces" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">necklaces</Link></li>
                <li><Link to="/shop?category=earrings" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">earrings</Link></li>
                <li><Link to="/shop?category=rings" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">rings</Link></li>
                <li><Link to="/shop?category=bags" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">bags</Link></li>
              </ul>
            </div>

            {/* About Links */}
            <div>
              <h4 
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  color: '#0D0D0B',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                }}
              >
                ABOUT
              </h4>
              <ul className="space-y-3">
                <li><Link to="/about" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">our story</Link></li>
                <li><Link to="/shipping" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">shipping & returns</Link></li>
              </ul>
            </div>

            {/* Customer Care */}
            <div>
              <h4 
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  color: '#0D0D0B',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                }}
              >
                CUSTOMER CARE
              </h4>
              <ul className="space-y-3">
                <li><a href="mailto:hello@shopplumo.com" style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 400, color: '#0D0D0B', letterSpacing: '0.05em' }} className="hover:opacity-60 transition-opacity">hello@shopplumo.com</a></li>
              </ul>
            </div>
          </div>

          {/* Newsletter - "Stay close." stays in script font */}
          <div className="mt-20 pt-12 border-t border-black/10">
            <div className="max-w-md">
              <h4 
                className="font-handwritten"
                style={{
                  fontSize: '28px',
                  color: '#1A1814',
                  marginBottom: '16px',
                }}
              >
                Stay close.
              </h4>
              <form className="flex gap-3">
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent border-b border-black/20 text-black px-0 py-2 placeholder:text-black/30 focus:border-black focus:outline-none transition-colors"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                />
                <button
                  type="submit"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    color: '#0D0D0B',
                  }}
                  className="hover:opacity-60 transition-opacity"
                >
                  join
                </button>
              </form>
            </div>
          </div>

          {/* Bottom - UPDATE 4: 11px, uppercase */}
          <div className="mt-16 flex flex-col md:flex-row justify-between items-center gap-4">
            <p 
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '11px',
                fontWeight: 400,
                letterSpacing: '0.15em',
                color: '#888',
                textTransform: 'uppercase',
              }}
            >
              © PLUMO 2026
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
